// src/pages/dashboard/components/CourierReturns.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, TrendingUp, ChevronRight } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import { usePermissions } from '../../../hooks/usePermissions';
import { getDashboardSummary } from '../../../services/api/returnBatch';
import { getUserWarehouses } from '../../../services/api/returnBatch';
import styles from './CourierReturns.module.css';

const CourierReturns = ({ currency }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    if (hasPermission('view_return_batches')) {
      fetchWarehouses();
    }
  }, []);

  useEffect(() => {
    if (hasPermission('view_return_batches')) {
      fetchSummary();
    }
  }, [selectedWarehouse]);

  const fetchWarehouses = async () => {
    try {
      const response = await getUserWarehouses();
      setWarehouses(response.warehouses || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedWarehouse) {
        params.warehouseId = selectedWarehouse;
      }
      const response = await getDashboardSummary(params);
      setData(response);
    } catch (err) {
      console.error('Error fetching courier returns:', err);
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  if (!hasPermission('view_return_batches')) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.courierReturns')}</h3>
        </div>
        <div className={styles.skeleton}>
          <div className={styles.skeletonBar}></div>
          <div className={styles.skeletonBar}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.courierReturns')}</h3>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchSummary} className={styles.retryButton}>
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { todayReturns, weekReturns } = data;
  const currentData = activeTab === 'today' ? todayReturns : weekReturns;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('dashboard.courierReturns')}</h3>
        <Link to="/return-batches" className={styles.viewAllLink}>
          {t('dashboard.viewAll')}
          <ChevronRight size={16} />
        </Link>
      </div>

      {warehouses.length > 1 && (
        <div className={styles.filters}>
          <select
            className={styles.warehouseSelect}
            value={selectedWarehouse || ''}
            onChange={(e) => setSelectedWarehouse(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">{t('dashboard.allWarehouses')}</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.tabButtons}>
          <button
            className={`${styles.tabButton} ${activeTab === 'today' ? styles.active : ''}`}
            onClick={() => setActiveTab('today')}
          >
            {t('dashboard.today')}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'week' ? styles.active : ''}`}
            onClick={() => setActiveTab('week')}
          >
            {t('dashboard.thisWeek')}
          </button>
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon}>
              <RotateCcw size={24} />
            </div>
            <div className={styles.summaryInfo}>
              <div className={styles.summaryValue}>{formatNumber(currentData.count)}</div>
              <div className={styles.summaryLabel}>{t('dashboard.returns')}</div>
              <div className={styles.summarySubValue}>
                {formatCurrency(currentData.totalValue, currency)}
              </div>
            </div>
          </div>
        </div>

        {currentData.courierBreakdown && currentData.courierBreakdown.length > 0 ? (
          <div className={styles.courierSection}>
            <h4 className={styles.sectionTitle}>{t('dashboard.courierBreakdown')}</h4>
            <div className={styles.courierList}>
              {currentData.courierBreakdown.map((courier, index) => (
                <div key={index} className={styles.courierItem}>
                  <div className={styles.courierHeader}>
                    <div className={styles.courierInfo}>
                      <div 
                        className={styles.courierDot} 
                        style={{ backgroundColor: courier.courierColorCode || '#94a3b8' }}
                      ></div>
                      <div className={styles.courierDetails}>
                        <span className={styles.courierName}>{courier.accountName}</span>
                        <span className={styles.courierCompany}>{courier.courierCompanyName}</span>
                      </div>
                    </div>
                    <span className={styles.courierCount}>{formatNumber(courier.returnCount)}</span>
                  </div>
                  <div className={styles.courierValue}>
                    {formatCurrency(courier.totalValue, currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <RotateCcw size={48} className={styles.emptyIcon} />
            <p>{t('dashboard.noReturns')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourierReturns;